import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  XMarkIcon,
  DocumentTextIcon,
  FolderOpenIcon,
  ArrowTopRightOnSquareIcon,
  TagIcon,
  UserIcon,
  ClockIcon,
  InformationCircleIcon,
  CloudArrowUpIcon,
  EyeIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { Badge, ApiLoader } from "../../ui";
import {
  getCustomerFrameworkDocumentsApi,
  acknowledgeDocumentApi,
  getCustomerDocumentFileApi as getCustomerDocumentBlobApi,
  getCustomerDocumentFileUrlApi,
  getCustomizedFileBlobApi,
  getCustomizedFileUrlApi,
  approveDocumentApi,
  flagDocumentApi,
} from "../../../services/axios/AuthApis/TrustCenterApis";
import { useSnackbar } from "../../../context/SnackbarContext";
import UploadFileModal from "../../ui/UploadFileModal";
import { useSelector } from "react-redux";
import { Dialog, DialogContent } from "../../ui/Modal";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import * as pdfjs from "pdfjs-dist";

import {
  getCustomerDocumentFileApi,
  uploadArtifactDocumentApi,
} from "../../../services/axios/AuthApis/TrustCenterFrameworkApis";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const API = process.env.REACT_APP_API_URL || "";
const APP_PRIMARY_COLOR = "var(--app-primary-color, #26256b)";
const IMAGE_EXTS = ["jpg", "jpeg", "png", "gif", "webp"];
const OFFICE_EXTS = ["doc", "docx", "xls", "xlsx", "ppt", "pptx"];
const extensionOf = (path) =>
  (path || "").split("?")[0].split(".").pop().toLowerCase();
// customer_admin -> Customer Admin
const readableRole = (role) =>
  role ? role.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) : "";

const ControlDocumentsModal = ({
  open,
  control,
  sourceType = "framework-master",
  onClose,
}) => {
  const userDetails = useSelector((state) => state.user);
  const userRole = userDetails?.user?.role || "";
  const canUpload =
    userRole === "customer_admin" ||
    userRole === "security_analyst" ||
    userRole === "end_user"; // Example role check
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showUploadFile, setShowUploadFile] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  // `silent` refreshes in place (after an upload) instead of showing the loader.
  const fetchDocs = useCallback(
    async ({ silent = false } = {}) => {
      if (!control?.id) return;
      if (!silent) setLoading(true);
      setError(null);
      try {
        let json;
        if (sourceType === "customer-framework") {
          json = await getCustomerFrameworkDocumentsApi(control.id);
        } else {
          const res = await fetch(
            `${API}/api/trust-center/framework-master/controls/${control.id}/documents/`,
            { headers: { "Content-Type": "application/json" } },
          );
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          json = await res.json();
        }
        const normalizedDocs = Array.isArray(json?.documents)
          ? json.documents
          : Array.isArray(json?.controls)
            ? json.controls
            : [];
        setDocs(normalizedDocs);
      } catch (err) {
        setError("Failed to load documents. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [control?.id, sourceType],
  );

  useEffect(() => {
    if (open) fetchDocs();
    else {
      setDocs([]);
      setError(null);
    }
  }, [open, fetchDocs]);

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* backdrop */}
          <motion.div
            key="doc-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
            onClick={onClose}
          />

          {/* slide-over panel */}
          <motion.div
            key="doc-panel"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 300 }}
            className="fixed inset-y-0 right-0 w-full max-w-xl bg-white shadow-2xl z-50 flex flex-col"
          >
            {/* header */}
            <div
              className="px-6 py-5 flex items-start justify-between gap-4 shrink-0"
              style={{
                background: `linear-gradient(90deg, color-mix(in srgb, ${APP_PRIMARY_COLOR} 85%, black 15%), ${APP_PRIMARY_COLOR})`,
              }}
            >
              <div className="flex items-start gap-3 min-w-0">
                <div className="bg-white/15 rounded-xl p-2 shrink-0 mt-0.5">
                  <FolderOpenIcon className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-white font-semibold text-base leading-tight truncate">
                    Related Documents
                  </h2>
                  <p className="text-white/80 text-xs mt-0.5 truncate">
                    {control?.control_id} · {control?.title}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-white/70 hover:text-white hover:bg-white/20 rounded-lg p-1.5 transition-colors shrink-0"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* body */}
            <div className="flex-1 overflow-y-auto p-6">
              {loading ? (
                <div className="flex justify-center py-16">
                  <ApiLoader label="Loading documents…" />
                </div>
              ) : error ? (
                <div className="text-center py-16 text-red-500 text-sm">
                  {error}
                </div>
              ) : docs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <DocumentTextIcon className="w-12 h-12 text-gray-300 mb-3" />
                  <p className="text-gray-500 font-medium text-sm">
                    No documents linked
                  </p>
                  <p className="text-gray-400 text-xs mt-1">
                    No document library entries are mapped to this control yet.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-xs text-gray-500 mb-2">
                    {docs.length} document{docs.length !== 1 ? "s" : ""} linked
                    to this control
                  </p>
                  {docs.map((doc) => (
                    <DocCard
                      key={doc.id}
                      doc={doc}
                      controlId={control?.id}
                      showUploadFile={showUploadFile}
                      setShowUploadFile={setShowUploadFile}
                      canUpload={canUpload}
                      userDetails={userDetails}
                      selectedDoc={selectedDoc}
                      setSelectedDoc={setSelectedDoc}
                      onClose={onClose}
                      refreshDocs={fetchDocs}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* footer */}
            <div className="border-t border-gray-100 px-6 py-4 bg-gray-50 shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="w-full py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
              >
                Close
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const DocCard = ({
  doc,
  controlId,
  showUploadFile,
  setShowUploadFile,
  canUpload,
  userDetails,
  onClose,
  selectedDoc,
  setSelectedDoc,
  refreshDocs,
}) => {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileUrl, setSelectedFileUrl] = useState(null);
  const [selectedFileExt, setSelectedFileExt] = useState(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [auditorMessage, setAuditorMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [auditorCompliant, setAuditorCompliant] = useState("yes"); // "yes" | "no"

  // Files are downloaded through the backend (the S3 bucket does not let the
  // browser read pre-signed links). PDFs go to the viewer as the file itself:
  // servers whose Content-Security-Policy has no blob: in connect-src stop
  // the PDF viewer from fetching a blob: link. Images use an object URL
  // (img-src allows blob:), freed on unmount.
  const objectUrlsRef = useRef([]);
  const toObjectUrl = (blob) => {
    const url = URL.createObjectURL(blob);
    objectUrlsRef.current.push(url);
    return url;
  };
  const toViewerSource = (blob, ext) =>
    ext === "pdf" ? blob : toObjectUrl(blob);
  useEffect(
    () => () =>
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url)),
    [],
  );

  // Primary evidence document inline viewer (End User only) — must be scrolled
  // to the end before "I Agree" becomes visible. Reaching the end never
  // auto-acknowledges; it only reveals the existing "I Agree" button.
  const [showPrimaryDocViewer, setShowPrimaryDocViewer] = useState(false);
  const [primaryDocUrl, setPrimaryDocUrl] = useState(null);
  const [primaryDocExt, setPrimaryDocExt] = useState(null);
  const [reachedDocEnd, setReachedDocEnd] = useState(false);
  const [primaryDocNumPages, setPrimaryDocNumPages] = useState(0);
  const [primaryDocViewerWidth, setPrimaryDocViewerWidth] = useState(600);
  const docScrollContainerRef = useRef(null);

  useEffect(() => {
    if (!showPrimaryDocViewer || !docScrollContainerRef.current) return;
    setPrimaryDocViewerWidth(
      Math.max(280, docScrollContainerRef.current.clientWidth - 32),
    );
  }, [showPrimaryDocViewer, primaryDocUrl]);

  // Checks the ACTUAL scroll container we render PDF pages / images into
  // (never the outer modal/panel) against scrollTop/clientHeight/scrollHeight,
  // with a small pixel tolerance for browser rounding. A previous version used
  // a raw <iframe src={presignedUrl}> for PDFs: since that URL is a different
  // origin, the browser's native PDF viewer inside the iframe owns its own
  // scroll and captures the user's wheel/touch input, so our outer wrapper's
  // scroll position never changed - "reached end" could never fire. Rendering
  // pages with react-pdf instead makes this container the real, own-origin
  // scroll element, so this check is accurate.
  const makeScrollEndHandler = (setReached) => (event) => {
    const el = event.currentTarget;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom <= 8) setReached(true);
  };
  const handlePrimaryDocScroll = makeScrollEndHandler(setReachedDocEnd);
  // Short document that never needs scrolling (content already fits the
  // container) - the end is trivially "reached" without requiring a scroll.
  const onPrimaryDocLastPageRendered = () => {
    const el = docScrollContainerRef.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight + 8) setReachedDocEnd(true);
  };

  // Customized file viewer ("Click here to view" under Customized Files) is a
  // SEPARATE dialog from the primary evidence viewer above and previously had
  // no scroll tracking or I Agree action at all - only Close. Same
  // scroll-to-end pattern, tracked independently since it's a different file.
  const [reachedCustomizedFileEnd, setReachedCustomizedFileEnd] =
    useState(false);
  const [customizedFileNumPages, setCustomizedFileNumPages] = useState(0);
  const [customizedFileViewerWidth, setCustomizedFileViewerWidth] =
    useState(600);
  const customizedFileScrollContainerRef = useRef(null);

  useEffect(() => {
    if (!showFileViewer || !customizedFileScrollContainerRef.current) return;
    setCustomizedFileViewerWidth(
      Math.max(280, customizedFileScrollContainerRef.current.clientWidth - 32),
    );
  }, [showFileViewer, selectedFileUrl]);

  const handleCustomizedFileScroll = makeScrollEndHandler(
    setReachedCustomizedFileEnd,
  );
  const onCustomizedFileLastPageRendered = () => {
    const el = customizedFileScrollContainerRef.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight + 8)
      setReachedCustomizedFileEnd(true);
  };

  const isEndUser = (userDetails?.user?.role || "") === "end_user";
  const isAuditor = (userDetails?.user?.role || "") === "auditor";
  const isCustomerAdmin = (userDetails?.user?.role || "") === "customer_admin";
  const ctrl = (doc.controls || []).find((c) => c.control_id === controlId);

  const handleSendAuditorMessage = async () => {
    const message = auditorMessage.trim();
    if (auditorCompliant === "no" && !message) {
      snackbar.error("A message is required when marking a control as Not Compliant.");
      return;
    }
    setSendingMessage(true);
    try {
      if (auditorCompliant === "no") {
        await flagDocumentApi(doc.id, controlId, message);
      } else {
        await approveDocumentApi(doc.id, controlId, message);
      }
      snackbar.success("Message sent to the customer.");
      setAuditorMessage("");
    } catch (err) {
      snackbar.error(
        err?.response?.data?.message ||
          "Failed to send message. Please try again.",
      );
      console.error("Send auditor message error:", err);
    } finally {
      setSendingMessage(false);
    }
  };

  // End User acknowledgement ("I Agree") for the control that opened this panel.
  // Uses the EXISTING endpoint POST /api/customer-framework/document-library/<doc_id>/acknowledge/
  // (acknowledgeDocumentApi) - same one the Document Library "I Agree" used before
  // End Users lost Document Library access. On success the control's
  // is_acknowledged becomes True -> it moves to the Auditor's "Ready for Audit"
  // queue. Auditors are blocked from this endpoint server-side (403).
  const handleAcknowledge = async (ctrl) => {
    if (!doc?.id || !ctrl?.control_id) return;
    setAcknowledging(true);
    try {
      const res = await acknowledgeDocumentApi(doc.id, ctrl.control_id);
      if (res?.success) {
        snackbar.success("Acknowledged. This control is now Ready for Audit.");
        if (typeof refreshDocs === "function")
          await refreshDocs({ silent: true });
      } else {
        snackbar.error(res?.message || res?.error || "Failed to acknowledge");
      }
    } catch (err) {
      snackbar.error(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to acknowledge. Please try again.",
      );
      console.error("Acknowledge error:", err);
    } finally {
      setAcknowledging(false);
    }
  };
  const handleArtifactUploadFile = async () => {
    if (!selectedFile) {
      snackbar.error("Please select a file to upload");
      return;
    }
    if (!selectedDoc?.id) {
      snackbar.error("No document selected");
      return;
    }
    try {
      const formData = new FormData();
      // Backend endpoint expects this exact multipart key.
      formData.append("documentFile", selectedFile);
      formData.append("employee_id", userDetails?.user?.id);
      formData.append("customer_doc_id", selectedDoc.id);
      // The control this panel was opened for; the file is listed under it.
      formData.append("control_id", controlId);
      await uploadArtifactDocumentApi(formData);
      setShowUploadFile(false);
      setSelectedFile(null);
      snackbar.success(
        isEndUser
          ? `${selectedFile.name} uploaded and sent for approval`
          : `${selectedFile.name} uploaded`,
      );
      // Show the new file under Customized Files straight away.
      if (typeof refreshDocs === "function")
        await refreshDocs({ silent: true });
    } catch (error) {
      if (error?.response?.status === 401) {
        snackbar.error("Your session has expired. Please log in again.");
      } else {
        snackbar.error(
          error?.response?.data?.message ||
            "Failed to upload file. Please try again.",
        );
      }
      console.error("Upload error:", error);
    }
  };
  const handleFileOpen = async () => {
    const ext = extensionOf(doc.file);
    try {
      let url;
      if (ext === "pdf" || IMAGE_EXTS.includes(ext)) {
        url = toViewerSource(await getCustomerDocumentBlobApi(doc.id), ext);
      } else {
        // Office formats render in Google's viewer, which needs a short-lived public link.
        const res = await getCustomerDocumentFileApi(doc.id);
        url = res?.data?.file;
      }
      if (!url) throw new Error("No file to open");
      // Open inline (instead of a new tab) so we can detect when the End
      // User has scrolled to the end of the document before revealing
      // "I Agree". Office formats still render via Google's viewer, embedded.
      setPrimaryDocExt(ext);
      setPrimaryDocUrl(url);
      setReachedDocEnd(false);
      setPrimaryDocNumPages(0);
      setShowPrimaryDocViewer(true);
    } catch (err) {
      snackbar.error("Failed to open file. Please try again.");
      console.error("Error", err);
    }
  };
  const handleViewFile = (doc) => () => {
    setSelectedDoc(doc);
    setShowUploadFile(true);
  };

  // "Click here to view" under Customized Files - opens the customer-uploaded
  // file for that control in a new tab.
  const viewCustomizedFileInNewTab = async (file) => {
    if (!file?.file_id) {
      snackbar.error("This file can't be opened. Please upload it again.");
      return;
    }
    try {
      const { url } = await getCustomizedFileUrlApi({
        document_id: doc.id,
        control_id: controlId,
        file_id: file.file_id,
        file_type: "customized",
      });
      if (!url) throw new Error("No link to the file");
      const ext = extensionOf(file.file);
      const target = OFFICE_EXTS.includes(ext)
        ? `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=false`
        : url;
      window.open(target, "_blank", "noopener,noreferrer");
    } catch (err) {
      snackbar.error("Failed to open file. Please try again.");
      console.error("Error opening file:", err);
    }
  };

  // "Acknowledge" - opens the file inline in a card with scroll-to-end + I Agree.
  const openCustomizedFile = async (file) => {
    const ext = extensionOf(file?.file);
    if (!file?.file_id) {
      snackbar.error("This file can't be opened. Please upload it again.");
      return;
    }
    if (OFFICE_EXTS.includes(ext)) {
      // Office files are shown inline via Google's viewer (embedded iframe)
      // in the same card, instead of a new tab - its scroll can't be
      // tracked, so "I Agree" is enabled as soon as the viewer loads.
      setSelectedFileExt(ext);
      setSelectedFileUrl(null);
      setReachedCustomizedFileEnd(true);
      setCustomizedFileNumPages(0);
      setFileLoading(true);
      setShowFileViewer(true);
      try {
        const { url } = await getCustomizedFileUrlApi({
          document_id: doc.id,
          control_id: controlId,
          file_id: file.file_id,
          file_type: "customized",
        });
        if (!url) throw new Error("No link to the file");
        setSelectedFileUrl(url);
      } catch (err) {
        setShowFileViewer(false);
        snackbar.error("Failed to open file. Please try again.");
        console.error("Error opening file:", err);
      } finally {
        setFileLoading(false);
      }
      return;
    }
    if (ext !== "pdf" && !IMAGE_EXTS.includes(ext)) {
      // Other (non-previewable) files open in a new tab from a short-lived link.
      try {
        const { url } = await getCustomizedFileUrlApi({
          document_id: doc.id,
          control_id: controlId,
          file_id: file.file_id,
          file_type: "customized",
        });
        if (!url) throw new Error("No link to the file");
        window.open(url, "_blank", "noopener,noreferrer");
      } catch (err) {
        snackbar.error("Failed to open file. Please try again.");
        console.error("Error opening file:", err);
      }
      return;
    }
    setSelectedFileExt(ext);
    setSelectedFileUrl(null);
    setReachedCustomizedFileEnd(false);
    setCustomizedFileNumPages(0);
    setFileLoading(true);
    setShowFileViewer(true);
    try {
      setSelectedFileUrl(
        toViewerSource(
          await getCustomizedFileBlobApi(doc.id, controlId, file.file_id),
          ext,
        ),
      );
    } catch (err) {
      setShowFileViewer(false);
      snackbar.error("Failed to open file. Please try again.");
      console.error("Error opening file:", err);
    } finally {
      setFileLoading(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      {/* card header */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-start gap-2">
        <DocumentTextIcon
          className="w-4 h-4 shrink-0 mt-0.5"
          style={{ color: APP_PRIMARY_COLOR }}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-gray-900 leading-snug">
              {doc.policy_procedure_guideline}
            </p>
            {(() => {
              const ctrl = (doc.controls || []).find(
                (c) => c.control_id === controlId,
              );
              return (
                ctrl?.is_customized && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700 whitespace-nowrap">
                    Customized
                  </span>
                )
              );
            })()}
          </div>
          {doc.serial_no && (
            <span className="text-xs text-gray-400">#{doc.serial_no}</span>
          )}
        </div>

        {canUpload && (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <button
              onClick={handleViewFile(doc)}
              className="p-1 transition-opacity opacity-80 hover:opacity-100"
              style={{ color: APP_PRIMARY_COLOR }}
              title="Upload file"
            >
              <CloudArrowUpIcon className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* card body */}
      <div className="px-4 py-3 space-y-2">
        {/* badges row */}
        <div className="flex flex-wrap gap-1.5">
          {doc.category && (
            <Badge variant="info" size="sm">
              <TagIcon className="w-3 h-3 mr-1 inline" />
              {doc.category}
            </Badge>
          )}
          {doc.control_domain && (
            <Badge variant="secondary" size="sm">
              {doc.control_domain}
            </Badge>
          )}
          {doc.review_frequency && (
            <Badge variant="warning" size="sm">
              <ClockIcon className="w-3 h-3 mr-1 inline" />
              {doc.review_frequency}
            </Badge>
          )}
        </div>

        {/* objective */}
        {doc.control_description_objective && (
          <Row icon={InformationCircleIcon} label="Objective">
            {doc.control_description_objective}
          </Row>
        )}

        {/* purpose */}
        {doc.purpose_coverage && (
          <Row icon={InformationCircleIcon} label="Purpose / Coverage">
            {doc.purpose_coverage}
          </Row>
        )}

        {/* evidence */}
        {doc.sample_implementation_evidence && (
          <Row icon={DocumentTextIcon} label="Sample Evidence">
            {doc.sample_implementation_evidence}
          </Row>
        )}

        {/* owner */}
        {doc.owner && (
          <Row icon={UserIcon} label="Owner">
            {doc.owner}
          </Row>
        )}
        {(() => {
          const isEndUser = (userDetails?.user?.role || "") === "end_user";
          const ctrl = (doc.controls || []).find(
            (c) => c.control_id === controlId,
          );
          const alreadyAcked = ctrl?.is_acknowledged;
          if (!doc.file || (isEndUser && alreadyAcked)) return null;
          return (
            <Row icon={EyeIcon} label="View Document">
              <button
                onClick={handleFileOpen}
                title="View evidence file"
                className="shrink-0 transition-opacity opacity-90 hover:opacity-100 hover:underline"
                style={{ color: APP_PRIMARY_COLOR }}
              >
                Click
              </button>
            </Row>
          );
        })()}

        {/* Acknowledgement section — only the control that opened this modal */}
        {(() => {
          const ctrl = (doc.controls || []).find(
            (c) => c.control_id === controlId,
          );
          if (!ctrl) return null;
          const isEndUser = (userDetails?.user?.role || "") === "end_user";
          const formatDate = (d) => {
            try {
              return d ? new Date(d).toLocaleDateString() : "";
            } catch {
              return "";
            }
          };
          return (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <div className="flex items-start gap-2">
                {ctrl.is_acknowledged ? (
                  <CheckCircleIcon className="w-3.5 h-3.5 text-green-500 shrink-0 mt-0.5" />
                ) : (
                  <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-300 shrink-0 mt-0.5" />
                )}
                <div>
                  <p
                    className={`text-xs font-semibold leading-tight ${ctrl.is_acknowledged ? "text-green-600" : "text-gray-400"}`}
                  >
                    {ctrl.is_acknowledged ? "Acknowledged" : "Not Acknowledged"}
                  </p>
                  {ctrl.is_acknowledged && (
                    <p className="text-[10px] text-gray-400 leading-tight mt-0.5">
                      by {readableRole(ctrl.acknowledged_user_role)}
                      {ctrl.acknowledgement_at
                        ? ` · ${formatDate(ctrl.acknowledgement_at)}`
                        : ""}
                    </p>
                  )}
                </div>
              </div>

              {/* End User "I Agree" — always rendered once a document/file has
                  been opened for review, but disabled until the viewer being
                  reviewed (primary evidence or customized file) reports its
                  own scroll-to-end. The actual click still only fires from
                  inside that viewer's dialog (see showPrimaryDocViewer /
                  showFileViewer below) — this is just the status readout.
                  Not shown to Auditor (Auditor Approves/Flags, never acknowledges). */}
              {isEndUser &&
                !ctrl.is_acknowledged &&
                !(reachedDocEnd || reachedCustomizedFileEnd) && (
                  <p className="mt-2 text-[10px] text-gray-400">
                    Scroll to the end of the document to enable "I Agree".
                  </p>
                )}
            </div>
          );
        })()}

        {/* Customized Document Files Section */}
        {(() => {
          const ctrl = (doc.controls || []).find(
            (c) => c.control_id === controlId,
          );
          if (
            !ctrl?.is_customized ||
            !ctrl?.customized_document_file ||
            ctrl.customized_document_file.length === 0
          ) {
            return null;
          }
          const formatDate = (d) => {
            try {
              return d ? new Date(d).toLocaleDateString() : "";
            } catch {
              return "";
            }
          };
          const getStatusColor = (status) => {
            switch (status?.toLowerCase()) {
              case "pending":
                return "bg-yellow-100 text-yellow-700";
              case "approved":
                return "bg-green-100 text-green-700";
              case "rejected":
                return "bg-red-100 text-red-700";
              default:
                return "bg-gray-100 text-gray-700";
            }
          };
          return (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                Customized Files ({ctrl.customized_document_file.length})
              </p>
              <div className="space-y-2">
                {ctrl.customized_document_file.map((file, idx) => (
                  <div
                    key={idx}
                    className="w-full p-2 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100 hover:border-gray-200 transition-colors text-left"
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <span className="text-[10px] text-gray-600 font-medium truncate hover:underline">
                        {`File ${idx + 1}`}
                      </span>
                      <span
                        className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${getStatusColor(file.status)}`}
                      >
                        {file.status || "pending"}
                      </span>
                    </div>
                    <div className="text-[9px] text-gray-500 space-y-0.5 flex items-center gap-2">
                      <p>
                        Uploaded by:{" "}
                        {file.uploaded_by_name
                          ? `${file.uploaded_by_name}${file.role ? ` (${readableRole(file.role)})` : ""}`
                          : readableRole(file.role) || "Unknown"}
                      </p>
                      <p>{formatDate(file.file_uploaded_at)}</p>
                      <button
                        onClick={() => viewCustomizedFileInNewTab(file)}
                        className="text-xs text-primary-700 underline hover:text-primary-900 ml-2"
                        style={{ minWidth: 0 }}
                      >
                        Click here to view
                      </button>
                      {isCustomerAdmin && (
                        <button
                          type="button"
                          onClick={() => openCustomizedFile(file)}
                          disabled={ctrl.is_acknowledged}
                          className="text-xs font-semibold text-white rounded px-2 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ backgroundColor: APP_PRIMARY_COLOR }}
                        >
                          {ctrl.is_acknowledged ? "Acknowledged" : "Acknowledge"}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Auditor Review (Compliant / Not Compliant + message) */}
        {(() => {
          const ctrl = (doc.controls || []).find(
            (c) => c.control_id === controlId,
          );
          if (!ctrl?.is_approved_by_auditor) return null;
          const isCompliant = ctrl.auditor_status === "reviewed";
          const latestComment = (ctrl.auditor_comments || [])[
            (ctrl.auditor_comments || []).length - 1
          ];
          return (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                  isCompliant
                    ? "bg-green-100 text-green-700"
                    : "bg-gray-200 text-gray-700"
                }`}
              >
                {isCompliant ? "Compliant" : "Not Compliant"}
              </span>
              {latestComment?.comment && (
                <p className="mt-1 text-xs text-gray-600">
                  <span className="font-semibold text-gray-700">Auditor Remarks:</span>{" "}
                  {latestComment.comment}
                </p>
              )}
            </div>
          );
        })()}

        {/* Auditor -> Customer message */}
        {isAuditor && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-3">
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                Compliant:
              </p>
              <label className="flex items-center gap-1.5 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={auditorCompliant === "yes"}
                  onChange={() => setAuditorCompliant("yes")}
                  disabled={sendingMessage}
                  className="w-4 h-4 accent-current cursor-pointer"
                  style={{ accentColor: APP_PRIMARY_COLOR }}
                />
                Yes
              </label>
              <label className="flex items-center gap-1.5 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={auditorCompliant === "no"}
                  onChange={() => setAuditorCompliant("no")}
                  disabled={sendingMessage}
                  className="w-4 h-4 cursor-pointer"
                  style={{ accentColor: "#dc2626" }}
                />
                No
              </label>
            </div>
            <div className="flex items-start gap-2 mb-2">
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide whitespace-nowrap mt-2">
                remarks:
              </p>
              <textarea
                rows={1}
                value={auditorMessage}
                onChange={(e) => setAuditorMessage(e.target.value)}
                placeholder={
                  auditorCompliant === "no"
                    ? "Message is required for Not Compliant…"
                    : "Type a message for the customer… (optional)"
                }
                className="flex-1 min-w-0 px-2 py-1 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 resize-none"
                style={{ "--tw-ring-color": APP_PRIMARY_COLOR }}
                disabled={sendingMessage}
              />
            </div>
            <div className="flex justify-end mt-2">
              <button
                type="button"
                onClick={handleSendAuditorMessage}
                disabled={
                  sendingMessage ||
                  (auditorCompliant === "no" && !auditorMessage.trim())
                }
                className="shrink-0 px-3 py-2 text-xs font-semibold text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: APP_PRIMARY_COLOR }}
              >
                {sendingMessage ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        )}
      </div>
      {/* showUploadFile is shared by every card; only the chosen document's card opens the dialog. */}
      {showUploadFile && canUpload && selectedDoc?.id === doc.id && (
        <UploadFileModal
          showUploadFile={showUploadFile}
          setShowUploadFile={setShowUploadFile}
          selectedFile={selectedFile || null}
          setSelectedFile={setSelectedFile}
          handleUploadFile={handleArtifactUploadFile}
          title="Upload Document File"
          subtitle="Upload documents file for completion"
          acceptedFileTypes=".pdf,.doc,.docx,.png,.jpg,.jpeg"
          maxFileSize={2}
          fileTypeLabel={["PDF", "DOC", "DOCX", "PNG", "JPG", "JPEG"]}
        />
      )}
      {showPrimaryDocViewer && primaryDocUrl && (
        <Dialog
          open={showPrimaryDocViewer}
          onOpenChange={setShowPrimaryDocViewer}
        >
          <DialogContent className="w-full max-w-3xl mx-auto bg-white rounded-lg shadow-lg p-4 flex flex-col items-center">
            <div
              ref={docScrollContainerRef}
              onScroll={handlePrimaryDocScroll}
              className="w-full h-[70vh] overflow-y-auto rounded border border-gray-100"
            >
              {(() => {
                const officeExts = [
                  "doc",
                  "docx",
                  "xls",
                  "xlsx",
                  "ppt",
                  "pptx",
                ];
                const imageExts = ["jpg", "jpeg", "png", "gif", "webp"];
                if (primaryDocExt === "pdf") {
                  return (
                    <Document
                      file={primaryDocUrl}
                      onLoadSuccess={({ numPages }) =>
                        setPrimaryDocNumPages(numPages)
                      }
                      onLoadError={() => setPrimaryDocNumPages(-1)}
                      loading={
                        <div className="flex items-center justify-center h-40">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                        </div>
                      }
                      error={
                        <p className="p-4 text-sm text-gray-500">
                          Failed to load PDF.
                        </p>
                      }
                    >
                      {Array.from(
                        { length: Math.max(primaryDocNumPages, 0) },
                        (_, i) => {
                          const isLast = i === primaryDocNumPages - 1;
                          return (
                            <div
                              key={`primary_page_${i + 1}`}
                              className="mb-4 flex justify-center shadow-sm"
                            >
                              <Page
                                pageNumber={i + 1}
                                width={primaryDocViewerWidth}
                                onRenderSuccess={
                                  isLast
                                    ? onPrimaryDocLastPageRendered
                                    : undefined
                                }
                              />
                            </div>
                          );
                        },
                      )}
                    </Document>
                  );
                } else if (officeExts.includes(primaryDocExt)) {
                  const googleViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(primaryDocUrl)}&embedded=true`;
                  return (
                    <iframe
                      src={googleViewerUrl}
                      title="Document Viewer"
                      className="w-full"
                      style={{ height: "250vh", border: "none" }}
                    />
                  );
                } else if (imageExts.includes(primaryDocExt)) {
                  return (
                    <img
                      src={primaryDocUrl}
                      alt="Document"
                      className="w-full h-auto"
                      onLoad={(e) => {
                        const el = docScrollContainerRef.current;
                        if (el && el.scrollHeight <= el.clientHeight + 8)
                          setReachedDocEnd(true);
                      }}
                    />
                  );
                }
                return (
                  <p className="p-4 text-sm text-gray-500">
                    Cannot preview this file type.
                  </p>
                );
              })()}
            </div>
            {!reachedDocEnd && (
              <p className="mt-2 text-xs text-gray-400">
                Scroll to the end of the document to continue.
              </p>
            )}
            <div className="mt-4 flex items-center gap-2 self-end">
              {(isEndUser || isCustomerAdmin) && ctrl && !ctrl.is_acknowledged && (
                <button
                  type="button"
                  disabled={!reachedDocEnd || acknowledging}
                  onClick={() => handleAcknowledge(ctrl)}
                  className="px-4 py-2 rounded text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: APP_PRIMARY_COLOR }}
                >
                  {acknowledging ? "Acknowledging…" : "I Agree"}
                </button>
              )}
              {(isEndUser || isCustomerAdmin) && ctrl?.is_acknowledged && (
                <span className="inline-flex items-center gap-1 text-sm font-medium text-green-600">
                  <CheckCircleIcon className="w-4 h-4" /> Acknowledged
                </span>
              )}
              <button
                className="px-4 py-2 rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
                onClick={() => setShowPrimaryDocViewer(false)}
              >
                Close
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {showFileViewer && (
        <Dialog open={showFileViewer} onOpenChange={setShowFileViewer}>
          <DialogContent className="w-full max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-4 flex flex-col items-center">
            <div
              ref={customizedFileScrollContainerRef}
              onScroll={handleCustomizedFileScroll}
              className="w-full h-[70vh] overflow-y-auto rounded"
            >
              {(() => {
                const ext = selectedFileExt;
                if (fileLoading || !selectedFileUrl) {
                  return (
                    <div className="flex items-center justify-center h-40">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                    </div>
                  );
                }
                if (ext === "pdf") {
                  return (
                    <Document
                      file={selectedFileUrl}
                      onLoadSuccess={({ numPages }) =>
                        setCustomizedFileNumPages(numPages)
                      }
                      onLoadError={() => setCustomizedFileNumPages(-1)}
                      loading={
                        <div className="flex items-center justify-center h-40">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                        </div>
                      }
                      error={
                        <p className="p-4 text-sm text-gray-500">
                          Failed to load PDF.
                        </p>
                      }
                    >
                      {Array.from(
                        { length: Math.max(customizedFileNumPages, 0) },
                        (_, i) => {
                          const isLast = i === customizedFileNumPages - 1;
                          return (
                            <div
                              key={`customized_page_${i + 1}`}
                              className="mb-4 flex justify-center shadow-sm"
                            >
                              <Page
                                pageNumber={i + 1}
                                width={customizedFileViewerWidth}
                                onRenderSuccess={
                                  isLast
                                    ? onCustomizedFileLastPageRendered
                                    : undefined
                                }
                              />
                            </div>
                          );
                        },
                      )}
                    </Document>
                  );
                } else if (IMAGE_EXTS.includes(ext)) {
                  return (
                    <img
                      src={selectedFileUrl}
                      alt="Document"
                      className="w-full h-auto"
                      onLoad={() => {
                        const el = customizedFileScrollContainerRef.current;
                        if (el && el.scrollHeight <= el.clientHeight + 8)
                          setReachedCustomizedFileEnd(true);
                      }}
                    />
                  );
                } else if (OFFICE_EXTS.includes(ext) && selectedFileUrl) {
                  const googleViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(selectedFileUrl)}&embedded=true`;
                  return (
                    <iframe
                      src={googleViewerUrl}
                      title="Document Viewer"
                      className="w-full"
                      style={{ height: "65vh", border: "none" }}
                    />
                  );
                } else {
                  return <p>Cannot preview this file type.</p>;
                }
              })()}
            </div>
            {!reachedCustomizedFileEnd && (
              <p className="mt-2 text-xs text-gray-400">
                Scroll to the end of the document to continue.
              </p>
            )}
            <div className="mt-4 flex items-center gap-2 self-end">
              {(isEndUser || isCustomerAdmin) && ctrl && !ctrl.is_acknowledged && (
                <button
                  type="button"
                  disabled={!reachedCustomizedFileEnd || acknowledging}
                  onClick={() => handleAcknowledge(ctrl)}
                  className="px-4 py-2 rounded text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: APP_PRIMARY_COLOR }}
                >
                  {acknowledging ? "Acknowledging…" : "I Agree"}
                </button>
              )}
              {(isEndUser || isCustomerAdmin) && ctrl?.is_acknowledged && (
                <span className="inline-flex items-center gap-1 text-sm font-medium text-green-600">
                  <CheckCircleIcon className="w-4 h-4" /> Acknowledged
                </span>
              )}
              <button
                className="px-4 py-2 rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
                onClick={() => setShowFileViewer(false)}
              >
                Close
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const Row = ({ icon: Icon, label, children }) => (
  <div className="flex gap-2 text-xs">
    <Icon className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-0.5" />
    <div>
      <span className="text-gray-400 font-medium uppercase tracking-wide text-[10px]">
        {label}:&nbsp;
      </span>
      <span className="text-gray-700 leading-snug">{children}</span>
    </div>
  </div>
);

export default ControlDocumentsModal;
